import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { setFormData, setExistingAttachments, addDeletedAttachmentId } from '../../../../app/projects/projectsSlice';
import { 
  useLazyGetApiAttachmentsQuery,
  useDeleteApiAttachmentsByIdMutation 
} from '../../../../app/athenaApiGen';
import { formatFileSize } from './Utilities';

// Context to share file objects with descriptions with parent CardModal
export const AttachmentContext = React.createContext({
  files: [], // Array of {file: File, description: string} objects
  setFiles: () => {},
});

export default function AttachmentForm() {
  const dispatch = useDispatch();
  const { formData, errors, modalMode, editingProjectId } = useSelector((state) => state.projects);
  const fileInputRef = React.useRef(null);
  const [duplicateWarning, setDuplicateWarning] = React.useState('');
  const [existingAttachments, setLocalExistingAttachments] = React.useState([]);
  const [deletedAttachmentIds, setDeletedAttachmentIds] = React.useState([]);
  const hasFetchedRef = React.useRef(false);
  const [fetchAttachments] = useLazyGetApiAttachmentsQuery();
  const [deleteAttachment] = useDeleteApiAttachmentsByIdMutation();
  
  // Get files from context (set by CardModal)
  const { files, setFiles } = React.useContext(AttachmentContext);

  // Reset fetch flag when modal mode or project changes
  React.useEffect(() => {
    hasFetchedRef.current = false;
  }, [modalMode, editingProjectId]);

  // Fetch existing attachments when in edit mode
  React.useEffect(() => {
    // console.log('📎 useEffect triggered:', { modalMode, editingProjectId, hasFetched: hasFetchedRef.current });
    
    // Only fetch if we haven't fetched yet and fetchAttachments is available
    if (modalMode === 'edit' && editingProjectId && !hasFetchedRef.current && fetchAttachments) {
      // console.log('📎 Fetching attachments for project:', editingProjectId);
      hasFetchedRef.current = true; // Mark as fetched to prevent re-fetching
      
      fetchAttachments({
        objectId: editingProjectId,
        objectType: 'Project',
      })
        .unwrap()
        .then((data) => {
          // console.log('📎 Fetched attachments data:', data);
          
          // Handle different response structures
          let attachments = [];
          if (data && data.attachments && Array.isArray(data.attachments)) {
            attachments = data.attachments;
          } else if (Array.isArray(data)) {
            attachments = data;
          }
          
          // console.log('📎 Setting existing attachments:', attachments.length, 'files');
          setLocalExistingAttachments(attachments);
        })
        .catch((error) => {
          console.error('📎 Error fetching existing attachments:', error);
          setLocalExistingAttachments([]);
          hasFetchedRef.current = false; // Allow retry on error
        });
    } else if (modalMode !== 'edit') {
      // Only clear if switching to create mode
      // console.log('📎 Switching to create mode, clearing attachments');
      setLocalExistingAttachments([]);
    }
  }, [modalMode, editingProjectId, fetchAttachments]);

  // Sync to Redux when existingAttachments changes (for Review component)
  React.useEffect(() => {
    if (dispatch) {
      dispatch(setExistingAttachments(existingAttachments));
    }
  }, [existingAttachments, dispatch]);

  const handleFileChange = (event) => {
    const newFiles = Array.from(event.target.files);

    // Filter out duplicate file names from:
    // 1. Files already selected in this session
    // 2. Files already attached to the project (in edit mode)
    const existingFileNames = files.map(f => f.file.name);
    const existingAttachmentNames = existingAttachments.map(a => a.name);
    const allExistingNames = [...existingFileNames, ...existingAttachmentNames];

    const uniqueNewFiles = newFiles.filter(file => !allExistingNames.includes(file.name));
    const duplicateFiles = newFiles.filter(file => allExistingNames.includes(file.name));
    
    if (duplicateFiles.length > 0) {
      // Show warning message to user
      const duplicateNames = duplicateFiles.map(f => f.name).join(', ');
      setDuplicateWarning(
        `${duplicateFiles.length} duplicate file(s) were not uploaded: ${duplicateNames}`
      );
      
      // Clear warning after 5 seconds
      setTimeout(() => setDuplicateWarning(''), 5000);
    }
    
    // Create file objects with descriptions
    const newFileObjects = uniqueNewFiles.map(file => ({
      file: file,
      description: ''
    }));
    
    const updatedFiles = [...files, ...newFileObjects];
    
    // Store file objects with descriptions in context
    setFiles(updatedFiles);
    
    // Store only file names in Redux for display
    dispatch(setFormData({
      attachmentNames: updatedFiles.map(f => f.file.name),
    }));
    
    // Reset the input so the same file can be selected again if removed and re-added
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    
    // Update context
    setFiles(updatedFiles);
    
    // Update Redux with file names only
    dispatch(setFormData({
      attachmentNames: updatedFiles.map(f => f.file.name),
    }));
  };
  
  const handleDescriptionChange = (index, description) => {
    const updatedFiles = [...files];
    updatedFiles[index] = {
      ...updatedFiles[index],
      description: description
    };
    setFiles(updatedFiles);
  };

  const handleDeleteExistingAttachment = async (attachmentId) => {
    try {
      await deleteAttachment({ id: attachmentId }).unwrap();
      // Capture name BEFORE filtering it out
      const deletedAttachment = existingAttachments.find(att => att.attachmentId === attachmentId);
      // Remove from local state
      const updatedAttachments = existingAttachments.filter(att => att.attachmentId !== attachmentId);
      setLocalExistingAttachments(updatedAttachments);
      // Update Redux state
      dispatch(setExistingAttachments(updatedAttachments));
      // Track deleted IDs for potential rollback or logging
      setDeletedAttachmentIds(prev => [...prev, attachmentId]);
      dispatch(addDeletedAttachmentId({id: attachmentId, name: deletedAttachment?.name?? 'Unknown'}));
      // console.log('Successfully deleted attachment:', attachmentId);
    } catch (error) {
      console.error('Error deleting attachment:', error);
      alert('Failed to delete attachment. Please try again.');
    }
  };

  // Debug logging
  // console.log('📎 AttachmentForm render:', {
  //   modalMode,
  //   editingProjectId,
  //   existingAttachmentsCount: existingAttachments.length,
  //   newFilesCount: files.length,
  //   existingAttachments: existingAttachments,
  //   existingAttachmentsIsArray: Array.isArray(existingAttachments),
  // });

  // Additional check - log when existingAttachments changes
  // React.useEffect(() => {
  //   console.log('📎 existingAttachments state changed:', existingAttachments.length, existingAttachments);
  // }, [existingAttachments]);

  return (
    <Stack spacing={3}>
      <Box key="upload-controls">
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Project Attachments
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          Upload documents, files, or other materials related to this project.
          You can upload multiple files at once.
        </Typography>

        <Button
          variant="outlined"
          component="label"
          startIcon={<AttachFileIcon />}
          sx={{ mb: 2 }}
        >
          Select Files
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={handleFileChange}
            accept="*/*"
          />
        </Button>

        {duplicateWarning && (
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'warning.main', 
              display: 'block', 
              mb: 2,
              p: 1,
              backgroundColor: 'warning.lighter',
              borderRadius: 1,
              border: 1,
              borderColor: 'warning.light',
            }}
          >
            {duplicateWarning}
          </Typography>
        )}

        {errors?.attachments && (
          <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mb: 2 }}>
            {errors.attachments}
          </Typography>
        )}
      </Box>

      {existingAttachments.length > 0 && (
        <Box key="existing-attachments">
          <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary' }}>
            Existing Attachments ({existingAttachments.length})
          </Typography>
          <Box
            sx={{
              maxHeight: '250px',
              overflowY: 'auto',
              overflowX: 'hidden',
              pr: 1,
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(0,0,0,0.05)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.2)',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.3)',
                },
              },
            }}
          >
            <Stack spacing={1}>
              {existingAttachments.map((attachment) => (
                <Box
                  key={attachment.attachmentId}
                  sx={{
                    p: 1.5,
                    border: 1,
                    borderColor: 'primary.light',
                    borderRadius: 1,
                    backgroundColor: 'primary.lighter',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                      <InsertDriveFileIcon sx={{ color: 'primary.main', fontSize: 24 }} />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {attachment.name}
                        </Typography>
                        {attachment.description && (
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                            {attachment.description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteExistingAttachment(attachment.attachmentId)}
                      sx={{ flexShrink: 0 }}
                      title="Delete attachment"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>
        </Box>
      )}

      {files.length > 0 && (
        <Box key="new-files">
          <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary' }}>
            New Files to Upload ({files.length})
          </Typography>
          <Box
            sx={{
              maxHeight: '300px',
              overflowY: 'auto',
              overflowX: 'hidden',
              pr: 1,
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(0,0,0,0.05)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.2)',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.3)',
                },
              },
            }}
          >
            <Stack spacing={1}>
              {files.map((fileObj, index) => (
              <Box
                key={`${fileObj.file.name}-${index}`}
                sx={{
                  p: 1.5,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  backgroundColor: 'background.paper',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                    <InsertDriveFileIcon sx={{ color: 'text.secondary', fontSize: 24 }} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {fileObj.file.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {formatFileSize(fileObj.file.size)}
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveFile(index)}
                    sx={{ flexShrink: 0 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Add a description (optional)"
                  value={fileObj.description}
                  onChange={(e) => handleDescriptionChange(index, e.target.value)}
                  multiline
                  rows={2}
                  sx={{ mt: 1 }}
                />
              </Box>
            ))}
          </Stack>
        </Box>
        </Box>
      )}

      {files.length === 0 && existingAttachments.length === 0 && (
        <Box
          key="empty-state"
          sx={{
            p: 4,
            border: 2,
            borderStyle: 'dashed',
            borderColor: 'divider',
            borderRadius: 2,
            textAlign: 'center',
            backgroundColor: 'background.paper',
          }}
        >
          <AttachFileIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            No files attached
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            Click "Select Files" above to attach documents
          </Typography>
        </Box>
      )}
    </Stack>
  );
}
